const S = (title, phase, checklist) => ({ title, phase, checklist });
const C = (step, owner) => [step, owner];

export const SECTIONS = {
  '0.1': S('Project Kickoff & Scope Definition', 'Phase 0 — Project Initiation & Planning', [
    C('Internal kickoff session held with full project team', 'PM'),
    C('All team members briefed on scope, roles, and delivery plan', 'PM'),
    C('External kickoff held with client', 'PM + FC'),
    C('Scope document prepared and shared with client', 'FC'),
    C('Client has signed the scope document', 'PM'),
    C('Kickoff MOM documented and stored within 24 hours', 'PM'),
    C('No development work started before scope sign-off', 'ALL'),
  ]),
  '0.2': S('Team Assignment & Role Definition', 'Phase 0 — Project Initiation & Planning', [
    C('All required roles identified: PM, FC, Developer(s), DevOps, Tester', 'PM'),
    C('Team members assigned to each role', 'PM'),
    C('Role assignment document prepared and shared with team', 'PM'),
    C('Each team member acknowledged their role in writing', 'All Members'),
    C('Reporting lines communicated to all team members', 'PM'),
    C('Team assignment sheet stored in documentation system', 'PM'),
  ]),
  '0.3': S('Project Setup in Tracking System', 'Phase 0 — Project Initiation & Planning', [
    C('Project created with correct name and client reference', 'PM'),
    C('Project start and end dates configured', 'PM'),
    C('Milestones defined and aligned to contract deliverables', 'PM'),
    C('All team members added with correct roles and access levels', 'PM'),
    C('Project structure shared with full team', 'PM'),
    C('Team members confirmed they have access before work begins', 'All Members'),
  ]),
  '1.1': S('Requirements Elicitation Process', 'Phase 1 — Requirements Gathering', [
    C('Requirements session scheduled with correct client stakeholders', 'PM + FC'),
    C('FC prepared agenda and discussion topics before the session', 'FC'),
    C('Session conducted and all requirements captured in structured format', 'FC'),
    C('PM documented MOM within 24 hours of session', 'PM'),
    C('All ambiguous requirements flagged and clarification requested in writing', 'FC'),
    C('Clarifications received and confirmed by client before proceeding', 'FC + PM'),
    C('Raw requirements document updated after every session', 'FC'),
  ]),
  '1.2': S('Requirements Documentation & Sign-Off', 'Phase 1 — Requirements Gathering', [
    C('All requirements documented in the official requirements document', 'FC'),
    C('Document reviewed and approved internally by PM before client sharing', 'PM'),
    C('Document shared with client with a defined review deadline', 'PM'),
    C('Client feedback received and addressed', 'FC'),
    C('Client has provided written sign-off on the final version', 'PM'),
    C('Signed document stored in central documentation system', 'PM'),
    C('No design or development started before sign-off confirmed', 'ALL'),
  ]),
  '1.3': S('Change Request Management', 'Phase 1 — Requirements Gathering', [
    C('Change identified and described in writing', 'FC or PM'),
    C('Impact assessed: scope, timeline, and cost implications documented', 'FC + PM'),
    C('Change Request form prepared by FC and reviewed by PM', 'FC → PM'),
    C('Change Request submitted to client with full impact details', 'PM'),
    C('Client has provided written approval including cost and timeline agreement', 'PM'),
    C('Requirements document updated with versioned change entry', 'FC'),
    C('Related tasks in tracking system updated to reflect the change', 'PM'),
    C('Work begins only after written approval confirmed', 'ALL'),
  ]),
  '2.1': S('SDD / LLD / BRD Preparation Standards', 'Phase 2 — Solution Design', [
    C('Signed requirements document received and used as basis for design', 'FC'),
    C('All minimum content areas covered: scope, flows, roles, constraints, specs', 'FC'),
    C('Internal review session held with Developer and DevOps', 'FC'),
    C('PM reviewed document for scope alignment with contract', 'PM'),
    C('All internal review feedback addressed', 'FC'),
    C('Document versioned starting from v0.1 with change log', 'FC'),
    C('Document ready at v1.0 for client sharing', 'FC'),
  ]),
  '2.2': S('Design Review & Client Approval Gate', 'Phase 2 — Solution Design', [
    C('Design document shared with client with a defined review deadline', 'PM'),
    C('Client feedback collected and documented', 'PM + FC'),
    C('FC addressed all feedback and incremented document version', 'FC'),
    C('Client has provided written sign-off on the final version', 'PM'),
    C('Signed document stored in central documentation system', 'PM'),
    C('Development task creation begins only after sign-off confirmed', 'PM'),
  ]),
  '3.1': S('Task Assignment & Handoff', 'Phase 3 — Development', [
    C('Task created in tracking system under the correct project', 'FC'),
    C('Title is clear and action-based', 'FC'),
    C('Description explains what needs to be done and why', 'FC'),
    C('Acceptance criteria defined in the task', 'FC'),
    C('Reference to design document or screen included if applicable', 'FC'),
    C('Task status correctly set to Open', 'FC'),
    C('Handoff briefing conducted with developer', 'FC'),
    C('Handoff documented as a comment in the task', 'FC'),
    C('Developer confirmed understanding via reply comment', 'Developer'),
    C('Task status confirmed as Open or In Progress before starting', 'Developer'),
    C('If Closed: written re-opening approval obtained from FC and PM', 'Developer'),
  ]),
  '3.2': S('Git Discipline & Version Control', 'Phase 3 — Development', [
    C('Checked out and pulled the latest testing branch before starting', 'Developer'),
    C('Merged testing into developer branch', 'Developer'),
    C('No merge conflicts — all resolved before proceeding', 'Developer'),
    C('Pull confirmation comment posted in task tracker', 'Developer'),
    C('Task status updated to In Progress after pull confirmed', 'Developer'),
    C('Development completed and code tested locally', 'Developer'),
    C('Pull Request opened to testing branch on version control platform', 'Developer'),
    C('PR description includes the task reference ID', 'Developer'),
    C('Peer reviewer assigned to the PR', 'Developer'),
    C('Peer reviewer has approved the PR', 'Reviewer'),
    C('Team notified after merge to testing branch', 'Developer'),
  ]),
  '3.3': S('Code Review Standards', 'Phase 3 — Development', [
    C('Self-review against task acceptance criteria completed', 'Developer'),
    C('Peer reviewer assigned before PR submission', 'Developer'),
    C('Code correctness verified against requirements', 'Reviewer'),
    C('Code quality and readability assessed', 'Reviewer'),
    C('Edge cases checked and handled appropriately', 'Reviewer'),
    C('No hardcoded values or debug code found', 'Reviewer'),
    C('No regression against existing functionality confirmed', 'Reviewer'),
    C('All feedback documented in Pull Request comments', 'Reviewer'),
    C('Developer addressed all review feedback', 'Developer'),
    C('PR approved only after all issues resolved', 'Reviewer'),
  ]),
  '3.4': S('Sprint Planning & Progress Tracking', 'Phase 3 — Development', [
    C('Sprint planning session held before sprint begins', 'PM + FC'),
    C('Sprint goal defined and communicated to full team', 'PM'),
    C('All tasks created with description, assignee, estimated effort, and due date', 'FC'),
    C('Team members confirm they understand their tasks', 'All Members'),
    C('Daily or every-other-day standup conducted', 'PM'),
    C('Blockers reported and resolved by PM within same day', 'PM'),
    C('Sprint review session held at end of sprint', 'PM + FC'),
    C('Completed tasks verified against acceptance criteria', 'FC + Tester'),
    C('Incomplete tasks analyzed for root cause', 'PM'),
    C('Sprint Review Summary documented and stored', 'PM'),
  ]),
  '4.1': S('Internal Testing & Bug Reporting', 'Phase 4 — Testing / QA', [
    C('Build deployed to testing environment before execution begins', 'DevOps'),
    C('Testing sheet prepared by FC and available to Tester', 'FC'),
    C('All test cases executed and results recorded: Pass, Fail, or Blocked', 'Tester'),
    C('All failed test cases logged as bugs with full details', 'Tester'),
    C('Bugs assigned to responsible developer immediately', 'FC or PM'),
    C('Bug fixes re-tested by Tester before closing', 'Tester'),
    C('No Critical or High bugs remain open before proceeding to UAT', 'PM'),
    C('Test Execution Report completed and stored', 'Tester'),
  ]),
  '4.2': S('Bug Lifecycle & Resolution Tracking', 'Phase 4 — Testing / QA', [
    C('Bug logged with: description, steps to reproduce, expected vs actual, severity, evidence', 'Tester'),
    C('Bug assigned to developer by FC or PM on the same day', 'FC or PM'),
    C('Bug status updated in tracking system at every stage', 'Developer + Tester'),
    C('SLA monitored — approaching SLA flagged to PM', 'PM'),
    C('Developer provides fix description when marking as Fixed', 'Developer'),
    C('Tester re-tests and updates to Closed or Reopened', 'Tester'),
    C('All Critical and High bugs closed before UAT readiness review', 'PM'),
    C('Bug log reviewed by PM daily during active testing cycles', 'PM'),
  ]),
  '5.1': S('UAT Delivery Readiness', 'Phase 5 — User Acceptance Testing', [
    C('Testing Sheet fully prepared and covers all UAT scenarios', 'FC'),
    C('Testing Sheet reviewed and approved by PM', 'PM'),
    C('User Manual updated and aligned with current build', 'FC'),
    C('User Manual reviewed and approved by PM', 'PM'),
    C('Developer Internal Validation Report completed', 'Developer'),
    C('Internal Validation Report reviewed by FC', 'FC'),
    C('No open Critical or High bugs from internal testing', 'PM'),
    C('PM has provided written sign-off on all three deliverables', 'PM'),
    C('UAT date communicated to customer only after PM written approval', 'PM'),
  ]),
  '5.2': S('UAT Session Facilitation', 'Phase 5 — User Acceptance Testing', [
    C('UAT session plan prepared and shared with client at least 2 working days before first session', 'FC + PM'),
    C('Formal kickoff communication sent to client', 'PM'),
    C('Environment access confirmed for all client participants before session', 'DevOps'),
    C('FC present and leading every UAT session', 'FC'),
    C('PM attending and capturing feedback in real time', 'PM'),
    C('Each scenario result recorded: Pass, Fail, or Deferred', 'FC'),
    C('No scenario marked as passed without explicit client confirmation', 'FC + PM'),
    C('Deferred scenarios tracked and scheduled for follow-up sessions', 'PM'),
    C('MOM documented and shared within 24 hours of each session', 'PM'),
    C('UAT Tracking Sheet updated after each session', 'FC'),
  ]),
  '5.3': S('UAT Defect Management', 'Phase 5 — User Acceptance Testing', [
    C('All defects raised during UAT logged in tracking system immediately', 'FC'),
    C('Each defect classified: Valid Bug, Enhancement, Training Issue, or Deferred', 'FC'),
    C('Classification approved by PM before communicating to client', 'PM'),
    C('Classification communicated to client in writing', 'PM'),
    C('Valid bugs fixed internally and re-tested before re-demonstration to client', 'Developer + Tester'),
    C('Client confirms resolution of each valid bug before closing', 'PM + FC'),
    C('Enhancement requests raised as formal Change Requests', 'PM'),
    C('Deferred items documented with client agreement in writing', 'PM'),
    C('Zero open valid bugs before requesting UAT sign-off', 'PM'),
    C('Client has signed the UAT Acceptance Document', 'PM'),
    C('Signed UAT Acceptance Document stored in project documentation system', 'PM'),
  ]),
  '6.1': S('Deployment Readiness & Control', 'Phase 6 — Deployment', [
    C('Deployment task created in tracking system with full scope description', 'DevOps'),
    C('Rollback plan documented in the deployment task', 'DevOps'),
    C('Build deployed to staging environment', 'DevOps'),
    C('FC validated build on staging and approved in tracking system', 'FC'),
    C('Tester validated all test cases on staging and approved in tracking system', 'Tester'),
    C('PM approved deployment timing in tracking system', 'PM'),
    C('All 3 written approvals confirmed before proceeding', 'PM'),
    C('Production deployment executed', 'DevOps'),
    C('Post-deployment smoke test performed', 'DevOps'),
    C('Smoke test results documented in deployment task within 2 hours', 'DevOps'),
    C('Issues escalated immediately to PM and FC if found', 'DevOps'),
    C('Rollback initiated if any critical issue discovered', 'DevOps + PM'),
  ]),
  '6.2': S('Production Go-Live Communication', 'Phase 6 — Deployment', [
    C('Go-Live Communication Plan prepared at least 3 working days before deployment', 'PM'),
    C('Pre-deployment notification sent to client at least 2 working days before go-live', 'PM'),
    C('Client receipt of notification confirmed', 'PM'),
    C('Status updates sent to client at agreed intervals during deployment', 'PM'),
    C('Post-deployment smoke test completed before sending confirmation', 'DevOps'),
    C('Go-live confirmation sent to client after successful deployment', 'PM'),
    C('All go-live communications stored in project documentation system', 'PM'),
  ]),
  '7.1': S('Milestone Sign-Off & Invoicing', 'Phase 7 — Project Closure & Handover', [
    C('All deliverables for the milestone completed and verified', 'PM + FC'),
    C('Milestone Completion Certificate prepared with all required fields', 'PM'),
    C('Certificate shared with client for review', 'PM'),
    C('Any client concerns addressed before requesting signature', 'PM + FC'),
    C('Client has signed the Milestone Completion Certificate', 'PM'),
    C('Signed certificate stored in project documentation system', 'PM'),
    C('Finance notified to issue invoice only after signed certificate received', 'PM'),
    C('No disputed milestones invoiced without COO approval', 'PM'),
  ]),
  '7.2': S('Knowledge Transfer & Documentation Handover', 'Phase 7 — Project Closure & Handover', [
    C('Full documentation package prepared by FC', 'FC'),
    C('Package includes: user manuals, config guides, technical docs, test results', 'FC'),
    C('Handover plan prepared by PM: what, format, sessions, timeline', 'PM'),
    C('Knowledge transfer sessions scheduled with client team', 'PM + FC'),
    C('All sessions conducted with FC present', 'FC'),
    C('Attendance records and MOM documented for each session', 'PM'),
    C('Client confirmed receipt and adequacy of all documentation in writing', 'PM'),
    C('Handover Confirmation Letter stored in project documentation system', 'PM'),
  ]),
  '7.3': S('Project Retrospective', 'Phase 7 — Project Closure & Handover', [
    C('Retrospective session scheduled within 5 working days of project closure', 'PM'),
    C('All key team members invited: PM, FC, Developer(s), DevOps', 'PM'),
    C('What went well — discussion held and documented', 'PM'),
    C('What did not go well — discussion and root causes documented', 'PM'),
    C('Actionable improvements defined for each identified issue', 'PM'),
    C('Lessons Learned Report prepared by PM', 'PM'),
    C('Report reviewed by COO', 'COO'),
    C('Findings shared with full team', 'PM + COO'),
  ]),
  'X.1': S('PM & FC Accountability & Tracking', 'Cross-Cutting — All Phases', [
    C('All new requests received today logged as tasks within 24 hours', 'PM + FC'),
    C('All open tasks reviewed and statuses updated before end of day', 'PM + FC'),
    C('Tasks stale for 2+ working days escalated or re-assigned', 'PM + FC'),
    C('All meeting action items logged as tasks immediately', 'PM + FC'),
    C('Weekly Status Report prepared and shared with COO — Every Sunday', 'PM'),
    C('Weekly Deliverables Report submitted to PM — Every Sunday', 'FC'),
    C('MOM issued for all meetings held this week within 24 hours', 'PM'),
    C('FC deliverable tracker per project updated with current statuses', 'FC'),
  ]),
  'X.2': S('Client Communication Standards', 'Cross-Cutting — All Phases', [
    C('PM is aware of all client communication before it is sent', 'PM'),
    C('Formal decisions and commitments are in writing — not verbal only', 'PM'),
    C('Verbal commitments followed up in writing within 24 hours', 'PM'),
    C('FC copies PM on all written communication with client', 'FC'),
    C('Response sent within the appropriate SLA for the communication type', 'PM'),
    C('Bad news or issues reviewed with COO before client communication', 'PM'),
    C('All client communications stored in project documentation system', 'PM'),
  ]),
  'X.3': S('Document Versioning & Storage', 'Cross-Cutting — All Phases', [
    C('Document created with version starting at v0.1', 'FC or PM'),
    C('Header includes: title, version, date, author, and status', 'FC or PM'),
    C('Change log entry added for every version increment', 'FC or PM'),
    C('Document stored in central documentation system immediately upon creation', 'FC or PM'),
    C('Stored under the correct project folder with correct naming', 'FC or PM'),
    C('Previous versions archived — not deleted', 'FC or PM'),
    C('Only PM or FC authorized to share documents externally — confirmed', 'PM'),
    C('Client-signed copy stored alongside original as official record', 'PM'),
  ]),
};
